import type { FC } from "react";

import { hasTotalUsersReachedMaxGroupSize } from "components/materials/page/groups/helpers";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuItem, SubMenu } from "mds/components/Menu";
import { AddPlusIcon, CopySendArrowIcon, CrossRemoveIcon, PersonGroupIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { NestedPageGroupType } from "store/selectors";
import { pageGroupUserSelectors } from "store/selectors/base";
import { toastLocalizedOperationCatcher } from "utils/alerts";

interface StudentOptionsMenuProps {
  studentSectionUserId: string;
  isInGroup?: boolean;
  currentGroupId?: string;
  page: PageType;
  pageGroups: NestedPageGroupType[];
  onRemove?: (studentSectionUserId: string) => void;
}

export const StudentOptionsMenu: FC<StudentOptionsMenuProps> = ({
  studentSectionUserId,
  isInGroup = false,
  currentGroupId,
  page,
  pageGroups,
  onRemove,
}) => {
  const allPageGroupUsers = useAppSelector(pageGroupUserSelectors.selectAll);

  // Find the current page group user record for this student in this group
  const currentPageGroupUser = allPageGroupUsers.find(
    (pgu) => pgu.section_user_id === studentSectionUserId && pgu.page_group_id === currentGroupId,
  );

  const onRemoveFromGroup = async () => {
    if (!currentPageGroupUser) {
      return;
    }
    try {
      await storeApi.page_group_users.destroy(currentPageGroupUser.id);
      onRemove?.(studentSectionUserId);
    } catch (e) {
      toastLocalizedOperationCatcher("failed_to_remove_from_group")(e);
    }
  };

  const onMoveToGroup = (targetGroupId: string) => () => {
    const target = pageGroups.find((g) => g.id === targetGroupId);
    if (target && hasTotalUsersReachedMaxGroupSize(target, page)) return;
    if (currentPageGroupUser) {
      // Student is already in a group - update their group
      storeApi.page_group_users
        .partial_update({
          id: currentPageGroupUser.id,
          page_group_id: targetGroupId,
          section_user_id: studentSectionUserId,
        })
        .catch(toastLocalizedOperationCatcher("failed_to_move_to_group"));
    } else {
      // Student is not in any group - create new page group user
      storeApi.page_group_users
        .create({
          page_group_id: targetGroupId,
          section_user_id: studentSectionUserId,
          section_id: page.section_id,
        })
        .catch(toastLocalizedOperationCatcher("failed_to_move_to_group"));
    }
  };

  const onCreateNewGroup = async () => {
    try {
      // Create new group first
      const newGroup = await storeApi.page_groups.create({
        page_id: page.id,
        section_id: page.section_id,
      });

      // Then move student to the new group
      if (currentPageGroupUser) {
        // Student is already in a group - update their group
        await storeApi.page_group_users.partial_update({
          id: currentPageGroupUser.id,
          page_group_id: newGroup.id,
          section_user_id: studentSectionUserId,
        });
      } else {
        // Student is not in any group - create new page group user
        await storeApi.page_group_users.create({
          page_group_id: newGroup.id,
          section_user_id: studentSectionUserId,
          section_id: page.section_id,
        });
      }
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_create_new_group")(error);
    }
  };

  return (
    <SidebarOptionsMenu>
      {isInGroup && currentPageGroupUser && (
        <MenuItem onClick={onRemoveFromGroup}>
          <IconText
            iconStart={<CrossRemoveIcon />}
            text={t("group_configuration_card.remove_from_group")}
          />
        </MenuItem>
      )}

      <SubMenu
        label={
          <IconText
            iconStart={<CopySendArrowIcon />}
            text={t("group_configuration_card.move_to_group")}
          />
        }
        overflow="auto"
        withArrow
      >
        {pageGroups.map((pageGroup, index) => {
          const isCurrentGroup = pageGroup.id === currentGroupId;
          const groupNumber = index + 1;
          const isFull = hasTotalUsersReachedMaxGroupSize(pageGroup, page);

          return (
            <MenuItem
              disabled={isCurrentGroup || isFull}
              key={pageGroup.id}
              onClick={onMoveToGroup(pageGroup.id)}
            >
              <IconText
                iconStart={<PersonGroupIcon />}
                text={t("common.group_title", { number: groupNumber })}
              />
            </MenuItem>
          );
        })}

        <MenuItem onClick={onCreateNewGroup}>
          <IconText iconStart={<AddPlusIcon />} text={t("group_configuration_card.new_group")} />
        </MenuItem>
      </SubMenu>
    </SidebarOptionsMenu>
  );
};
